import type { Bilingual, Confidence, Exercise, SmallDrones, TerrainReport, WeatherCustom, ZoneLetter } from "../../api/types";
import { type Draft, loadDraft, storedDraftFor } from "../../views/missionDraft";
import { weatherKey } from "../../weather";

/** Météo dans l'analyse du terrain (chantier de cohérence météo) : forme du bloc `weather` du rapport
 *  (`terrain_weather.as_dict` du serveur), qualificatif du coefficient sur l'échelle des ordres, et entrées du
 *  dossier affiché (météo, date, fuseau) gardées avec le rapport pour dire s'il est périmé. Types propres à
 *  l'analyse, à côté de `TerrainReport` : la carte ne les lit pas (seules les données déjà dessinées changent). */

/** Effet de la météo sur le terrain, par facteur ; conséquences pour l'ami (selon la posture) et pour l'ENI. */
export interface TerrainWeatherEffect {
  factor: "visibility" | "trafficability" | "flood" | "frozen" | "cover" | "wind" | "airspace" | "delays";
  text: Bilingual;
  friendly: Bilingual | null;
  enemy: Bilingual | null;
}

/** Valeurs de la même zone par beau temps, données à côté de celles du jour (temps actif seulement). */
export interface TerrainWeatherFair {
  mobility: { go: number; slow: number; no_go: number } | null;
  mobility_coeff: number;
  observation_pct: number | null;
  threat_pct: number;
  aptitude: TerrainReport["aptitude"];
  verdict_badge: Bilingual;
}

export type GroundState = "normal" | "frozen" | "wet" | "light_snow" | "soaked" | "thaw" | "snow" | "deep_snow" | "icy";

/** Météo de l'analyse : origine (saison, scénario du catalogue, météo choisie), appréciation d'ensemble,
 *  conditions, état du sol retenu, effets ; `active` faux : par ce temps, l'analyse est celle du beau temps. */
export interface TerrainWeather {
  key: string;
  source: "seasonal" | "scenario" | "custom";
  fallback: boolean;
  month: number;
  climate: string;
  label: Bilingual;
  assessment: Bilingual;
  severity: number;
  active: boolean;
  pictogram: string;
  visibility_km: [number, number];
  visibility_open: boolean;
  ceiling_ft: number | null;
  wind_kmh: [number, number];
  temp: [number, number];
  ground: GroundState;
  ground_label: Bilingual;
  icy_roads: boolean;
  flood: boolean;
  observation_limit_km: number | null;
  direct_fire_cap_km: number | null;
  observation_pct: number | null;
  high_point_in_cloud: boolean;
  flying: "ok" | "limited" | "suspended";
  /** État des petits drones (chantier « Drones », `drones.SmallDrones`) ; absent d'un serveur antérieur. */
  small_drones?: SmallDrones | null;
  delay_pct: number;
  effects: TerrainWeatherEffect[];
  fair: TerrainWeatherFair | null;
}

/** Qualificatif du coefficient de mobilité, sur l'échelle unique des ordres et de la mesure au survol. */
export interface MobilityLevel {
  label: Bilingual;
  tone: "ok" | "warn" | "brand";
}

/** Météo, date et fuseau du dossier affiché, envoyés avec l'analyse. */
export interface TerrainInputs {
  start_dtg: string | null;
  time_zone: ZoneLetter | null;
  weather_scenario: string | null;
  weather_custom: WeatherCustom | null;
}

/** Ce que le rapport garde de ses entrées : météo (clé composée), début et fuseau. */
export interface TerrainInputsKey {
  weather: string;
  start_dtg: string | null;
  time_zone: string | null;
}

/** Rapport d'analyse avec ce que le chantier y ajoute (bloc météo, qualificatif, entrées et heure du calcul). */
export type TerrainReportWeather = TerrainReport & {
  weather?: TerrainWeather | null;
  mobility_level?: MobilityLevel;
  inputs?: TerrainInputsKey;
  /** Heure du calcul (ms), qui date le cartouche. */
  computed_at?: number;
};

export function withWeather(report: TerrainReport): TerrainReportWeather {
  return report as TerrainReportWeather;
}

/** Confiance de la rubrique Météo (moyenne pour une estimation saisonnière), null sans météo. */
export function weatherConfidence(report: TerrainReport): Confidence | null {
  return (report.confidence as TerrainReport["confidence"] & { weather?: Confidence | null }).weather ?? null;
}

/** Pont détruit du dossier : listé par l'analyse, jamais compté comme un franchissement. */
export function destroyedBridge(bridge: TerrainReport["obstacles"]["bridges"][number]): boolean {
  return Boolean((bridge as { destroyed?: boolean }).destroyed);
}

type Dossier = Pick<Exercise, "id" | "version" | "name" | "language" | "map_slug" | "parameters">;

/** Brouillon du dossier affiché, gardé sur ce poste : celui qui modifie le dossier enregistré (`storedDraftFor`),
 *  celui d'un dossier à créer, ou null (dossier pas encore chargé, ou sans brouillon). */
export function displayedDraft(exerciseId: string | null, exercise: Dossier | undefined): Draft | null {
  if (exerciseId) return exercise ? storedDraftFor(exercise) : null;
  return loadDraft(undefined).draft;
}

/** Entrées de l'analyse : une même source pour la météo, la date et le fuseau — le brouillon gardé sur ce poste
 *  s'il y en a un (la carte affiche alors son dispositif), sinon le dossier enregistré. En CENTAC, aucune météo
 *  choisie : la météo se rédige dans l'ordre de la rotation. */
export function terrainInputs(exercise: Pick<Exercise, "parameters"> | undefined, draft: Pick<Draft, "parameters"> | null): TerrainInputs {
  const p = draft?.parameters ?? exercise?.parameters;
  const centac = p?.level === "CENTAC";
  return {
    start_dtg: p?.start_dtg ?? null,
    time_zone: p?.time_zone ?? null,
    weather_scenario: centac ? "auto" : (p?.weather_scenario ?? null),
    weather_custom: centac ? null : (p?.weather_custom ?? null),
  };
}

export function inputsKey(inputs: TerrainInputs): TerrainInputsKey {
  return { weather: weatherKey(inputs) ?? "auto", start_dtg: inputs.start_dtg, time_zone: inputs.time_zone };
}

/** Le rapport a-t-il été calculé pour une autre météo, une autre date ou un autre fuseau que ceux du dossier
 *  affiché ? Un rapport sans entrées gardées (calculé avant) n'est jamais dit périmé. */
export function staleReport(report: TerrainReport, current: TerrainInputs): boolean {
  const kept = withWeather(report).inputs;
  if (!kept) return false;
  const now = inputsKey(current);
  return kept.weather !== now.weather || kept.start_dtg !== now.start_dtg || kept.time_zone !== now.time_zone;
}
